/**
 * 输入框按 Enter 键安全触发事件的 Vue 指令
 * 处理输入法（IME）合字状态，避免在中文/日文等输入法选词期间触发 Enter 键事件。
 *
 * iOS Safari 兼容说明：
 *   该浏览器的事件顺序为 compositionend → keydown(Enter)，导致 keydown 触发时
 *   isComposing 已被同步重置为 false，e.isComposing 也为 false。
 *   此处记录 compositionend 时间戳，在 keydown 中判断距上次 compositionend 是否
 *   在 50ms 内，作为兜底拦截。50ms 内人类无法完成"选词→主动按发送 Enter"的操作，
 *   不会误拦截正常发送；setTimeout(0) 方案在多次连续输入或设备繁忙时存在竞态，故弃用。
 * @example
 * <input v-enter-safe="handleEnter" />
 * <textarea v-enter-safe="handleEnter" />
 * <el-input v-enter-safe="handleEnter" />
 */

/** IME 选词后 Enter 的最大允许延迟（ms），用于 iOS Safari 时序兜底 */
const IME_ENTER_GUARD_MS = 50;

export default {
  mounted(el, binding) {
    let isComposing = false;
    let lastCompositionEndAt = 0;

    const onCompositionStart = () => {
      isComposing = true;
    };

    const onCompositionEnd = () => {
      isComposing = false;
      // 记录时间戳，供 iOS Safari 兜底：compositionend 先于 keydown 触发，
      // keydown 到达时 isComposing 已为 false，需靠时间窗口判断是否为 IME Enter
      lastCompositionEndAt = Date.now();
    };

    const onKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      // 标准 IME 状态 | 浏览器原生 isComposing | iOS Safari 时间窗口兜底
      const isImeActive = isComposing || e.isComposing || Date.now() - lastCompositionEndAt < IME_ENTER_GUARD_MS;
      if (!isImeActive) {
        binding.value?.(e);
      }
    };

    el.__vEnterSafe__ = { onCompositionStart, onCompositionEnd, onKeyDown };

    el.addEventListener('compositionstart', onCompositionStart);
    el.addEventListener('compositionend', onCompositionEnd);
    el.addEventListener('keydown', onKeyDown);
  },
  unmounted(el) {
    const { onCompositionStart, onCompositionEnd, onKeyDown } = el.__vEnterSafe__ || {};
    el.removeEventListener('compositionstart', onCompositionStart);
    el.removeEventListener('compositionend', onCompositionEnd);
    el.removeEventListener('keydown', onKeyDown);
    delete el.__vEnterSafe__;
  }
};
